import classNames from 'classnames';
import { animate, motion } from 'motion/react';
import React__default, { createContext, useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Button } from '../atoms/Button.js';
import { toAlignmentAttributes } from '../layouts/Alignment/alignment.js';
import { toSpacingAttributes } from '../layouts/Spacing/spacing.js';
import './ContentSlider.scss';

// ─── Context & Hook ─────────────────────────────────────────────────────────
const ContentSliderContext = createContext(null);
const defaultContextValue = {
    nextPage: () => { },
    prevPage: () => { },
    nextItem: () => { },
    prevItem: () => { },
    gotoPage: () => { },
    hasNextItem: false,
    hasNextPage: false,
    hasPrevItem: false,
    hasPrevPage: false,
    currentPage: 0,
    totalPages: 0,
    selectedIndex: undefined,
    scrollContainerRef: { current: null },
    axis: 'x',
    stopScrollAnimation: () => { },
    scrollToPosition: () => { },
    scrollToIndex: () => { },
};
/**
 * Create a controller to access ContentSlider state from outside its tree.
 *
 * Pass the returned controller to both `<ContentSlider controller={slider} />`
 * and `<PrevItemButton contextValue={slider.contextValue} />`.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its component
function useContentSliderController() {
    const [contextValue, setContextValue] = useState(defaultContextValue);
    return useMemo(() => ({ contextValue, setContextValue }), [contextValue]);
}
// ─── Helpers ────────────────────────────────────────────────────────────────
const springConfig = { type: 'spring', visualDuration: 0.3, bounce: 0.05 };
/**
 * Calculate page boundaries using the Motion+ approach:
 * each page scrolls until the first obscured item becomes visible.
 */
function calculatePageBoundaries(items, containerSize, totalScrollSize) {
    if (items.length === 0 || containerSize <= 0 || totalScrollSize <= containerSize) {
        return [0];
    }
    const maxScroll = totalScrollSize - containerSize;
    const pages = [0];
    let scrollPos = 0;
    while (scrollPos < maxScroll - 1) {
        const viewEnd = scrollPos + containerSize;
        // Find first item whose end extends past the visible area
        let nextTarget = maxScroll;
        for (const item of items) {
            if (item.start + item.size > viewEnd + 1) {
                nextTarget = Math.min(item.start, maxScroll);
                break;
            }
        }
        if (nextTarget <= scrollPos)
            break; // prevent infinite loop
        pages.push(nextTarget);
        scrollPos = nextTarget;
    }
    return pages;
}
/** Read the current scroll position and max-scroll from a scroll container, or return null if the ref is empty. */
function getScrollMetrics(ref, isHorizontal) {
    const el = ref.current;
    if (!el)
        return null;
    const scrollPos = isHorizontal ? el.scrollLeft : el.scrollTop;
    const maxScroll = (isHorizontal ? el.scrollWidth : el.scrollHeight) -
        (isHorizontal ? el.offsetWidth : el.offsetHeight);
    return { el, scrollPos, maxScroll };
}
/** Find the page whose boundary is closest to the given scroll position. */
function getPageFromScroll(scrollPos, pages) {
    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < pages.length; i++) {
        const dist = Math.abs(scrollPos - pages[i]);
        if (dist < minDist) {
            minDist = dist;
            closest = i;
        }
    }
    return closest;
}
// ─── Content fade helpers ────────────────────────────────────────────────────
const FADE_MAX = 16; // px — maximum fade gradient size
/** Sigmoid base function for smooth fade easing. */
function baseSigmoid(t) {
    return 1 / (1 + Math.exp(-1 * (t * 12 - 1)));
}
const SIGMOID_MIN = baseSigmoid(0);
const SIGMOID_RANGE = 1 / (baseSigmoid(1) - SIGMOID_MIN);
/** Maps 0→1 through a sigmoid curve so the fade grows quickly then plateaus. */
function fadeSigmoid(t) {
    return (baseSigmoid(t) - SIGMOID_MIN) * SIGMOID_RANGE;
}
/** Compute the fade size (px) for a given scroll distance from the edge. */
function computeFadeSize(scrollDistance) {
    const ratio = scrollDistance / FADE_MAX;
    return Math.min(fadeSigmoid(ratio) * FADE_MAX, FADE_MAX);
}
// ─── Component ──────────────────────────────────────────────────────────────
const ContentSlider = ({ items, itemsSpacing, itemsAlignX, itemsAlignY, itemSize = 'auto', axis = 'x', showNativeScrollbar = false, className, children, controller, onItemPress, selectedIndex, trackPadding, isKeyboardDisabled = false, initialScrollIndex, hasContentFade = true, rounding, }) => {
    const wrapperRef = useRef(null);
    const scrollRef = useRef(null);
    const isHorizontal = axis === 'x';
    // ─── Page state ─────────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const currentPageRef = useRef(0);
    const pageBoundaries = useRef([0]);
    const itemPositionsRef = useRef([]);
    const didInitialScroll = useRef(false);
    const initialScrollIndexRef = useRef(initialScrollIndex);
    const [isAtScrollStart, setIsAtScrollStart] = useState(true);
    const [isAtScrollEnd, setIsAtScrollEnd] = useState(true);
    const isAtScrollStartRef = useRef(true);
    const isAtScrollEndRef = useRef(true);
    // ─── Scroll animation ───────────────────────────────────────────────
    const scrollAnimation = useRef(null);
    const scrollTargetRef = useRef(null);
    const stopScrollAnimation = useCallback(() => {
        if (scrollAnimation.current) {
            scrollAnimation.current.stop();
            scrollAnimation.current = null;
            scrollTargetRef.current = null;
            // Re-enable snap after animation was cancelled
            const el = scrollRef.current;
            if (el)
                el.style.scrollSnapType = '';
        }
    }, []);
    /** Animate to a scroll position using a Motion spring, with snap
     *  disabled during the animation so it doesn't fight the spring. */
    const scrollToPosition = useCallback((target) => {
        const el = scrollRef.current;
        if (!el)
            return;
        stopScrollAnimation();
        const prop = isHorizontal ? 'scrollLeft' : 'scrollTop';
        const start = el[prop];
        // If we're already at the target, nothing to animate
        if (Math.abs(start - target) < 1)
            return;
        // Disable snap during spring animation
        el.style.scrollSnapType = 'none';
        scrollTargetRef.current = target;
        scrollAnimation.current = animate(start, target, {
            ...springConfig,
            onUpdate: (v) => {
                el[prop] = v;
            },
            onComplete: () => {
                // Re-enable snap
                el.style.scrollSnapType = '';
                scrollAnimation.current = null;
                scrollTargetRef.current = null;
            },
        });
    }, [isHorizontal, stopScrollAnimation]);
    // ─── Measurement ────────────────────────────────────────────────────
    const measureItems = useCallback(() => {
        const el = scrollRef.current;
        if (!el)
            return [];
        const itemEls = el.querySelectorAll('.content-slider-item--rev');
        return Array.from(itemEls).map((item) => {
            const htmlItem = item;
            return {
                start: isHorizontal ? htmlItem.offsetLeft : htmlItem.offsetTop,
                size: isHorizontal ? htmlItem.offsetWidth : htmlItem.offsetHeight,
            };
        });
    }, [isHorizontal]);
    /** Write fade gradient sizes as CSS custom properties on the wrapper.
     *  Called on every scroll frame — bypasses React state for performance.
     *  Suppresses the fade on a side when the selected item overlaps its zone. */
    const updateContentFade = useCallback((scrollPos, maxScroll) => {
        const wrapper = wrapperRef.current;
        const el = scrollRef.current;
        if (!wrapper || !el || !hasContentFade)
            return;
        const scrollEnd = maxScroll - scrollPos;
        let fadeStart = computeFadeSize(scrollPos);
        let fadeEnd = computeFadeSize(scrollEnd);
        // If the selected item sits inside the fade zone, suppress that
        // side so its outline isn't clipped by the mask.
        if (selectedIndex != null) {
            const positions = itemPositionsRef.current;
            if (selectedIndex < positions.length) {
                const item = positions[selectedIndex];
                const viewSize = isHorizontal ? el.offsetWidth : el.offsetHeight;
                const itemStartInView = item.start - scrollPos;
                const itemEndInView = itemStartInView + item.size;
                if (itemStartInView < fadeStart)
                    fadeStart = 0;
                if (itemEndInView > viewSize - fadeEnd)
                    fadeEnd = 0;
            }
        }
        wrapper.style.setProperty('--contentSlider-fadeStart', `${fadeStart}px`);
        wrapper.style.setProperty('--contentSlider-fadeEnd', `${fadeEnd}px`);
    }, [hasContentFade, selectedIndex, isHorizontal]);
    const updateScrollBounds = useCallback((scrollPos, maxScroll) => {
        const atStart = scrollPos <= 1;
        const atEnd = maxScroll <= 0 || scrollPos >= maxScroll - 1;
        if (atStart !== isAtScrollStartRef.current) {
            isAtScrollStartRef.current = atStart;
            setIsAtScrollStart(atStart);
        }
        if (atEnd !== isAtScrollEndRef.current) {
            isAtScrollEndRef.current = atEnd;
            setIsAtScrollEnd(atEnd);
        }
    }, []);
    const measure = useCallback(() => {
        const metrics = getScrollMetrics(scrollRef, isHorizontal);
        if (!metrics)
            return;
        const { el, scrollPos, maxScroll } = metrics;
        const cs = isHorizontal ? el.offsetWidth : el.offsetHeight;
        const ss = cs + maxScroll;
        const itemPositions = measureItems();
        itemPositionsRef.current = itemPositions;
        const pages = calculatePageBoundaries(itemPositions, cs, ss);
        pageBoundaries.current = pages;
        setTotalPages(pages.length);
        const page = getPageFromScroll(scrollPos, pages);
        currentPageRef.current = page;
        setCurrentPage(page);
        updateScrollBounds(scrollPos, maxScroll);
        updateContentFade(scrollPos, maxScroll);
    }, [isHorizontal, measureItems, updateScrollBounds, updateContentFade]);
    useEffect(() => {
        measure();
        // Apply initial scroll position after first measurement (once only)
        if (!didInitialScroll.current) {
            didInitialScroll.current = true;
            const idx = initialScrollIndexRef.current;
            if (idx != null && idx > 0) {
                const el = scrollRef.current;
                const positions = itemPositionsRef.current;
                if (el && idx < positions.length) {
                    const pos = positions[idx].start;
                    if (isHorizontal) {
                        el.scrollLeft = pos;
                    }
                    else {
                        el.scrollTop = pos;
                    }
                }
            }
        }
        const el = scrollRef.current;
        if (!el)
            return;
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        // Also observe items so we re-measure when their sizes change
        const itemEls = el.querySelectorAll('.content-slider-item--rev');
        itemEls.forEach((item) => ro.observe(item));
        return () => ro.disconnect();
    }, [measure, items.length, isHorizontal]);
    // ─── Navigation ─────────────────────────────────────────────────────
    const hasNextItem = !isAtScrollEnd;
    const hasNextPage = currentPage < totalPages - 1;
    const hasPrevItem = !isAtScrollStart;
    const hasPrevPage = currentPage > 0;
    const gotoPage = useCallback((page) => {
        const clamped = Math.max(0, Math.min(page, pageBoundaries.current.length - 1));
        const target = pageBoundaries.current[clamped] ?? 0;
        currentPageRef.current = clamped;
        setCurrentPage(clamped);
        const metrics = getScrollMetrics(scrollRef, isHorizontal);
        if (metrics)
            updateScrollBounds(target, metrics.maxScroll);
        scrollToPosition(target);
    }, [isHorizontal, scrollToPosition, updateScrollBounds]);
    const nextPage = useCallback(() => {
        gotoPage(currentPageRef.current + 1);
    }, [gotoPage]);
    const prevPage = useCallback(() => {
        gotoPage(currentPageRef.current - 1);
    }, [gotoPage]);
    const nextItem = useCallback(() => {
        const metrics = getScrollMetrics(scrollRef, isHorizontal);
        if (!metrics)
            return;
        const { maxScroll } = metrics;
        const scrollPos = scrollTargetRef.current ?? metrics.scrollPos;
        const positions = itemPositionsRef.current;
        if (positions.length === 0)
            return;
        for (const item of positions) {
            if (item.start > scrollPos + 1) {
                const target = Math.min(item.start, maxScroll);
                const page = getPageFromScroll(target, pageBoundaries.current);
                currentPageRef.current = page;
                setCurrentPage(page);
                updateScrollBounds(target, maxScroll);
                scrollToPosition(target);
                return;
            }
        }
    }, [isHorizontal, scrollToPosition, updateScrollBounds]);
    const prevItem = useCallback(() => {
        const metrics = getScrollMetrics(scrollRef, isHorizontal);
        if (!metrics)
            return;
        const { maxScroll } = metrics;
        const scrollPos = scrollTargetRef.current ?? metrics.scrollPos;
        const positions = itemPositionsRef.current;
        if (positions.length === 0)
            return;
        for (let i = positions.length - 1; i >= 0; i--) {
            if (positions[i].start < scrollPos - 1) {
                const target = Math.max(positions[i].start, 0);
                const page = getPageFromScroll(target, pageBoundaries.current);
                currentPageRef.current = page;
                setCurrentPage(page);
                updateScrollBounds(target, maxScroll);
                scrollToPosition(target);
                return;
            }
        }
    }, [isHorizontal, scrollToPosition, updateScrollBounds]);
    // ─── Scroll tracking ────────────────────────────────────────────────
    const handleScroll = useCallback(() => {
        const metrics = getScrollMetrics(scrollRef, isHorizontal);
        if (!metrics)
            return;
        const { scrollPos, maxScroll } = metrics;
        updateScrollBounds(scrollPos, maxScroll);
        updateContentFade(scrollPos, maxScroll);
        // Only update page from scroll when NOT running a programmatic animation
        // (gotoPage already sets the page eagerly)
        if (!scrollAnimation.current) {
            const page = getPageFromScroll(scrollPos, pageBoundaries.current);
            if (page !== currentPageRef.current) {
                currentPageRef.current = page;
                setCurrentPage(page);
            }
        }
    }, [isHorizontal, updateScrollBounds, updateContentFade]);
    // Cancel any running spring animation when the user starts a native
    // scroll gesture (wheel / trackpad / touch).
    useEffect(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        const cancel = () => stopScrollAnimation();
        el.addEventListener('wheel', cancel, { passive: true });
        el.addEventListener('touchstart', cancel, { passive: true });
        return () => {
            el.removeEventListener('wheel', cancel);
            el.removeEventListener('touchstart', cancel);
        };
    }, [stopScrollAnimation]);
    // ─── Mouse drag ─────────────────────────────────────────────────────
    // Touch / trackpad / wheel scrolling is handled natively by the browser.
    // We only add custom pointer handling for mouse (and pen) drag.
    // Do not start drag when the pointer is on a focusable/interactive element
    // so that inputs, buttons, dropdowns, etc. receive focus and clicks.
    const isDragging = useRef(false);
    const dragStartPos = useRef(0);
    const dragStartScroll = useRef(0);
    const dragDistance = useRef(0);
    const pointerDownTarget = useRef(null);
    /** Recent pointer samples for velocity calculation on release. */
    const dragSamples = useRef([]);
    const CLICK_THRESHOLD = 5; // px — below this movement counts as a click, not a drag
    const getItemIndexFromTarget = useCallback((target) => {
        let node = target;
        const el = scrollRef.current;
        while (node && node !== el) {
            if (node instanceof HTMLElement &&
                node.classList.contains('content-slider-item--rev')) {
                const parent = node.parentElement;
                if (parent) {
                    const siblings = parent.querySelectorAll('.content-slider-item--rev');
                    for (let i = 0; i < siblings.length; i++) {
                        if (siblings[i] === node)
                            return i;
                    }
                }
                return null;
            }
            node = node.parentNode;
        }
        return null;
    }, []);
    const isPointerOnInteractiveElement = useCallback((target) => {
        let node = target;
        const el = scrollRef.current;
        while (node && node !== el) {
            if (node instanceof HTMLElement) {
                const tag = node.tagName?.toLowerCase();
                if (tag === 'input' ||
                    tag === 'textarea' ||
                    tag === 'select' ||
                    tag === 'button' ||
                    (tag === 'a' && node.href))
                    return true;
                if (node.getAttribute('contenteditable') === 'true')
                    return true;
                const role = node.getAttribute('role');
                const interactiveRoles = [
                    'button',
                    'link',
                    'textbox',
                    'searchbox',
                    'combobox',
                    'listbox',
                    'menuitem',
                    'option',
                    'slider',
                    'spinbutton',
                    'switch',
                    'tab',
                ];
                if (role && interactiveRoles.includes(role))
                    return true;
            }
            node = node.parentNode;
        }
        return false;
    }, []);
    const handlePointerDown = useCallback((e) => {
        if (e.pointerType === 'touch')
            return; // native touch scroll
        if (isPointerOnInteractiveElement(e.target))
            return;
        e.preventDefault();
        // preventDefault suppresses the default focus action, so
        // explicitly focus the wrapper for keyboard access.
        wrapperRef.current?.focus();
        const el = scrollRef.current;
        if (!el)
            return;
        stopScrollAnimation();
        // Disable snap during drag so mandatory snap doesn't fight scrollLeft updates
        el.style.scrollSnapType = 'none';
        e.currentTarget.setPointerCapture(e.pointerId);
        isDragging.current = true;
        dragDistance.current = 0;
        pointerDownTarget.current = e.target;
        const pos = isHorizontal ? e.clientX : e.clientY;
        dragStartPos.current = pos;
        dragStartScroll.current = isHorizontal ? el.scrollLeft : el.scrollTop;
        dragSamples.current = [{ pos, time: e.timeStamp }];
    }, [isHorizontal, isPointerOnInteractiveElement, stopScrollAnimation]);
    const handlePointerMove = useCallback((e) => {
        if (e.pointerType === 'touch')
            return;
        if (!isDragging.current)
            return;
        e.preventDefault();
        const el = scrollRef.current;
        if (!el)
            return;
        const pos = isHorizontal ? e.clientX : e.clientY;
        const delta = pos - dragStartPos.current;
        dragDistance.current = Math.abs(delta);
        if (isHorizontal) {
            el.scrollLeft = dragStartScroll.current - delta;
        }
        else {
            el.scrollTop = dragStartScroll.current - delta;
        }
        // Record sample for velocity calculation (keep last 5)
        const samples = dragSamples.current;
        samples.push({ pos, time: e.timeStamp });
        if (samples.length > 5)
            samples.shift();
    }, [isHorizontal]);
    const handlePointerUp = useCallback((e) => {
        if (e.pointerType === 'touch')
            return;
        if (!isDragging.current)
            return;
        isDragging.current = false;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        catch {
            /* already released */
        }
        const metrics = getScrollMetrics(scrollRef, isHorizontal);
        if (!metrics)
            return;
        const { el, scrollPos } = metrics;
        // If movement was below the click threshold, treat as a press
        if (onItemPress && dragDistance.current < CLICK_THRESHOLD) {
            const index = getItemIndexFromTarget(pointerDownTarget.current);
            if (index !== null) {
                el.style.scrollSnapType = '';
                onItemPress(index);
                return;
            }
        }
        // Calculate drag velocity from recent pointer samples.
        // Velocity is in pointer-space (px/ms), so a negative value
        // means the pointer moved left/up → scroll increases → next page.
        const samples = dragSamples.current;
        let velocity = 0;
        if (samples.length >= 2) {
            const oldest = samples[0];
            const newest = samples[samples.length - 1];
            const dt = newest.time - oldest.time;
            if (dt > 0 && dt < 200) {
                velocity = (newest.pos - oldest.pos) / dt;
            }
        }
        const closestPage = getPageFromScroll(scrollPos, pageBoundaries.current);
        // A fast flick overrides the closest-page logic: advance one
        // page in the flick direction for a responsive swipe feel.
        const FLICK_THRESHOLD = 0.3; // px/ms
        let targetPage = closestPage;
        if (Math.abs(velocity) > FLICK_THRESHOLD) {
            if (velocity < 0) {
                // Pointer moved left/up → scroll forward
                targetPage = Math.min(closestPage + 1, pageBoundaries.current.length - 1);
            }
            else {
                // Pointer moved right/down → scroll backward
                targetPage = Math.max(closestPage - 1, 0);
            }
        }
        const target = pageBoundaries.current[targetPage] ?? 0;
        currentPageRef.current = targetPage;
        setCurrentPage(targetPage);
        if (Math.abs(scrollPos - target) > 1) {
            // Keep snap disabled — scrollToPosition re-enables it on complete.
            scrollToPosition(target);
        }
        else {
            // Already at the target; just re-enable snap.
            el.style.scrollSnapType = '';
        }
    }, [isHorizontal, scrollToPosition, onItemPress, getItemIndexFromTarget]);
    // ─── Keyboard ───────────────────────────────────────────────────────
    // Attached as a native listener so preventDefault() fires directly on
    // the element rather than through React's delegated event system.
    useEffect(() => {
        if (isKeyboardDisabled)
            return;
        const el = wrapperRef.current;
        if (!el)
            return;
        const handleKeyDown = (e) => {
            const prevKey = isHorizontal ? 'ArrowLeft' : 'ArrowUp';
            const nextKey = isHorizontal ? 'ArrowRight' : 'ArrowDown';
            if (e.key !== prevKey && e.key !== nextKey)
                return;
            e.preventDefault();
            // Ignore held-key repeats — they interrupt the spring animation
            // mid-flight and cause visible jitter. Discrete presses still
            // advance immediately so users can tap quickly to move through pages.
            if (e.repeat)
                return;
            if (e.key === prevKey) {
                prevPage();
            }
            else {
                nextPage();
            }
        };
        el.addEventListener('keydown', handleKeyDown);
        return () => el.removeEventListener('keydown', handleKeyDown);
    }, [isKeyboardDisabled, isHorizontal, prevPage, nextPage]);
    // ─── Scroll to index ────────────────────────────────────────────────
    const scrollToIndex = useCallback((index) => {
        const metrics = getScrollMetrics(scrollRef, isHorizontal);
        if (!metrics)
            return;
        const { el, scrollPos } = metrics;
        const positions = itemPositionsRef.current;
        if (index < 0 || index >= positions.length)
            return;
        const { start: itemStart, size: itemSize } = positions[index];
        const containerSize = isHorizontal ? el.offsetWidth : el.offsetHeight;
        // Account for scroll-padding so the JS scroll target matches where
        // CSS scroll-snap will land. Without this, the spring animation
        // scrolls to the wrong position and snap corrects it with a visible jump.
        const style = getComputedStyle(el);
        const scrollPaddingStart = parseFloat(isHorizontal ? style.scrollPaddingInlineStart : style.scrollPaddingBlockStart) || 0;
        const scrollPaddingEnd = parseFloat(isHorizontal ? style.scrollPaddingInlineEnd : style.scrollPaddingBlockEnd) || 0;
        const adjustedStart = itemStart - scrollPaddingStart;
        const visibleEnd = scrollPos + containerSize - scrollPaddingEnd;
        if (adjustedStart < scrollPos) {
            // Item is before the visible area — scroll to its snap-aligned start
            scrollToPosition(adjustedStart);
        }
        else if (itemStart + itemSize > visibleEnd) {
            // Item extends past the visible area — find the latest snap-aligned
            // position that still shows the full item. Walk backwards from the
            // target item to find the first item whose start puts the target
            // fully in view.
            let target = adjustedStart;
            for (let i = index; i >= 0; i--) {
                const candidateStart = positions[i].start - scrollPaddingStart;
                if (candidateStart + containerSize - scrollPaddingEnd >= itemStart + itemSize) {
                    target = candidateStart;
                }
                else {
                    break;
                }
            }
            scrollToPosition(target);
        }
    }, [isHorizontal, scrollToPosition]);
    // ─── Context ────────────────────────────────────────────────────────
    const contextValue = useMemo(() => ({
        nextPage,
        prevPage,
        nextItem,
        prevItem,
        gotoPage,
        scrollToIndex,
        hasNextItem,
        hasNextPage,
        hasPrevItem,
        hasPrevPage,
        currentPage,
        totalPages,
        selectedIndex,
        scrollContainerRef: scrollRef,
        axis,
        stopScrollAnimation,
        scrollToPosition,
    }), [
        nextPage,
        prevPage,
        nextItem,
        prevItem,
        gotoPage,
        scrollToIndex,
        hasNextItem,
        hasNextPage,
        hasPrevItem,
        hasPrevPage,
        currentPage,
        totalPages,
        selectedIndex,
        axis,
        stopScrollAnimation,
        scrollToPosition,
    ]);
    useEffect(() => {
        controller?.setContextValue(contextValue);
    }, [controller, contextValue]);
    return (React__default.createElement(ContentSliderContext.Provider, { value: contextValue },
        React__default.createElement("div", { ref: wrapperRef, "data-rev-component": 'content-slider', "data-rev-axis": axis, "data-rev-item-size": itemSize, "data-rev-native-scrollbar": showNativeScrollbar || undefined, "data-rev-content-fade": hasContentFade || undefined, "data-rev-rounding": rounding, className: className, style: trackPadding
                ? { '--contentSlider-trackPadding': trackPadding }
                : undefined, tabIndex: isKeyboardDisabled ? -1 : 0, role: 'region', "aria-label": 'Content slider' },
            React__default.createElement("div", { ref: scrollRef, className: 'content-slider-track--rev', tabIndex: -1, onScroll: handleScroll, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerUp, onPointerCancel: handlePointerUp, ...toSpacingAttributes('g', itemsSpacing), ...toAlignmentAttributes('x', itemsAlignX), ...toAlignmentAttributes('y', itemsAlignY) }, items.map((item, index) => (React__default.createElement("div", { key: index, className: 'content-slider-item--rev', "data-rev-pressable": onItemPress ? true : undefined, "data-rev-selected": selectedIndex === index ? true : undefined, onClick: onItemPress
                    ? (e) => {
                        if (isPointerOnInteractiveElement(e.target))
                            return;
                        onItemPress(index);
                    }
                    : undefined }, item))))),
        children));
};
function StyledScrollbar({ contextValue, className, isInverted, margin, }) {
    const { scrollContainerRef, axis, stopScrollAnimation, scrollToPosition } = contextValue;
    const isHorizontal = axis === 'x';
    const trackRef = useRef(null);
    const thumbRef = useRef(null);
    const isDragging = useRef(false);
    const dragStartPointer = useRef(0);
    const dragStartScroll = useRef(0);
    const updateThumb = useCallback(() => {
        const el = scrollContainerRef.current;
        const thumb = thumbRef.current;
        const track = trackRef.current;
        if (!el || !thumb || !track)
            return;
        const containerSize = isHorizontal ? el.offsetWidth : el.offsetHeight;
        const scrollSize = isHorizontal ? el.scrollWidth : el.scrollHeight;
        if (scrollSize <= containerSize) {
            thumb.style.display = 'none';
            return;
        }
        thumb.style.display = '';
        const trackSize = isHorizontal ? track.offsetWidth : track.offsetHeight;
        const thumbSize = Math.max(24, (containerSize / scrollSize) * trackSize);
        const scrollPos = isHorizontal ? el.scrollLeft : el.scrollTop;
        const maxScroll = scrollSize - containerSize;
        const thumbPos = maxScroll > 0 ? (scrollPos / maxScroll) * (trackSize - thumbSize) : 0;
        if (isHorizontal) {
            thumb.style.width = `${thumbSize}px`;
            thumb.style.height = '';
            thumb.style.left = `${thumbPos}px`;
            thumb.style.top = '';
        }
        else {
            thumb.style.height = `${thumbSize}px`;
            thumb.style.width = '';
            thumb.style.top = `${thumbPos}px`;
            thumb.style.left = '';
        }
    }, [scrollContainerRef, isHorizontal]);
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el)
            return;
        const onScroll = () => updateThumb();
        el.addEventListener('scroll', onScroll, { passive: true });
        const ro = new ResizeObserver(() => updateThumb());
        ro.observe(el);
        updateThumb();
        return () => {
            el.removeEventListener('scroll', onScroll);
            ro.disconnect();
        };
    }, [scrollContainerRef, updateThumb]);
    useEffect(() => {
        updateThumb();
    }, [updateThumb, contextValue.totalPages]);
    const handleTrackPointerDown = useCallback((e) => {
        const el = scrollContainerRef.current;
        const track = trackRef.current;
        const thumb = thumbRef.current;
        if (!el || !track || !thumb)
            return;
        if (thumb.contains(e.target))
            return;
        e.preventDefault();
        const trackRect = track.getBoundingClientRect();
        const pointerPos = isHorizontal
            ? e.clientX - trackRect.left
            : e.clientY - trackRect.top;
        const trackSize = isHorizontal ? track.offsetWidth : track.offsetHeight;
        const containerSize = isHorizontal ? el.offsetWidth : el.offsetHeight;
        const scrollSize = isHorizontal ? el.scrollWidth : el.scrollHeight;
        const maxScroll = scrollSize - containerSize;
        const thumbSize = Math.max(24, (containerSize / scrollSize) * trackSize);
        const ratio = Math.max(0, Math.min(1, (pointerPos - thumbSize / 2) / (trackSize - thumbSize)));
        const rawTarget = ratio * maxScroll;
        // Snap to the nearest item boundary so the animation lands on a
        // scroll-snap position and the browser doesn't jank-correct after.
        const itemEls = el.querySelectorAll('.content-slider-item--rev');
        let target = rawTarget;
        let closestDist = Infinity;
        itemEls.forEach((item) => {
            const htmlItem = item;
            const itemStart = isHorizontal ? htmlItem.offsetLeft : htmlItem.offsetTop;
            const dist = Math.abs(itemStart - rawTarget);
            if (dist < closestDist) {
                closestDist = dist;
                target = Math.min(itemStart, maxScroll);
            }
        });
        stopScrollAnimation();
        scrollToPosition(target);
    }, [scrollContainerRef, isHorizontal, stopScrollAnimation, scrollToPosition]);
    const handleThumbPointerDown = useCallback((e) => {
        const el = scrollContainerRef.current;
        if (!el)
            return;
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        isDragging.current = true;
        dragStartPointer.current = isHorizontal ? e.clientX : e.clientY;
        dragStartScroll.current = isHorizontal ? el.scrollLeft : el.scrollTop;
        stopScrollAnimation();
        el.style.scrollSnapType = 'none';
    }, [scrollContainerRef, isHorizontal, stopScrollAnimation]);
    const handleThumbPointerMove = useCallback((e) => {
        if (!isDragging.current)
            return;
        e.preventDefault();
        const el = scrollContainerRef.current;
        const track = trackRef.current;
        if (!el || !track)
            return;
        const pointerDelta = (isHorizontal ? e.clientX : e.clientY) - dragStartPointer.current;
        const trackSize = isHorizontal ? track.offsetWidth : track.offsetHeight;
        const containerSize = isHorizontal ? el.offsetWidth : el.offsetHeight;
        const scrollSize = isHorizontal ? el.scrollWidth : el.scrollHeight;
        const maxScroll = scrollSize - containerSize;
        const thumbSize = Math.max(24, (containerSize / scrollSize) * trackSize);
        const scrollDelta = (pointerDelta / (trackSize - thumbSize)) * maxScroll;
        const newScroll = Math.max(0, Math.min(maxScroll, dragStartScroll.current + scrollDelta));
        if (isHorizontal) {
            el.scrollLeft = newScroll;
        }
        else {
            el.scrollTop = newScroll;
        }
    }, [scrollContainerRef, isHorizontal]);
    const handleThumbPointerUp = useCallback((e) => {
        if (!isDragging.current)
            return;
        isDragging.current = false;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        catch {
            /* already released */
        }
        const el = scrollContainerRef.current;
        if (el)
            el.style.scrollSnapType = '';
    }, [scrollContainerRef]);
    return (React__default.createElement("div", { "data-rev-component": 'content-slider-scrollbar', "data-rev-axis": axis, "data-rev-inverted": isInverted ?? undefined, ref: trackRef, className: className, ...toSpacingAttributes('m', margin), onPointerDown: handleTrackPointerDown },
        React__default.createElement("div", { ref: thumbRef, className: 'content-slider-scrollbar-thumb--rev', onPointerDown: handleThumbPointerDown, onPointerMove: handleThumbPointerMove, onPointerUp: handleThumbPointerUp, onPointerCancel: handleThumbPointerUp })));
}
// ─── Pagination Dots ────────────────────────────────────────────────────
const DOT_SIZE = 6;
const DOT_GAP = 5;
const DOT_CONTAINER_PADDING = 6;
function PaginationDots({ contextValue, maxDots = 7, className }) {
    const { currentPage, totalPages, gotoPage } = contextValue;
    // Track which direction the user last moved so the dot window can
    // bias toward the direction of travel (the "look-ahead" shift).
    // Derived synchronously during render to avoid the extra re-render
    // that a state + useEffect approach would cause.
    const prevPageRef = useRef(currentPage);
    const lastDirectionRef = useRef(null);
    if (currentPage !== prevPageRef.current) {
        lastDirectionRef.current = currentPage > prevPageRef.current ? 'right' : 'left';
        prevPageRef.current = currentPage;
    }
    if (totalPages <= 1)
        return null;
    const needsWindowing = totalPages > maxDots;
    // Compute the sliding window of visible dots once per render.
    // A single center-shift-clamp pass replaces three discrete branches,
    // which avoids the 2-position jump the old approach caused when the
    // directional shift pushed the middle branch away from a clamped edge.
    let windowStart = 0;
    let windowEnd = totalPages - 1;
    if (needsWindowing) {
        const half = Math.floor((maxDots - 1) / 2);
        windowStart = currentPage - half;
        const dir = lastDirectionRef.current;
        if (dir === 'right')
            windowStart--;
        else if (dir === 'left')
            windowStart++;
        windowStart = Math.max(0, Math.min(windowStart, totalPages - maxDots));
        windowEnd = windowStart + maxDots - 1;
    }
    // The first and last dots always render at full size (anchors);
    // dots at the window edges (windowStart, windowEnd) shrink as overflow indicators.
    const isBeyond = (i) => i !== 0 && i !== totalPages - 1 && (i <= windowStart || i >= windowEnd);
    const containerMaxWidth = maxDots * DOT_SIZE + (maxDots - 2) * DOT_GAP - 4 + DOT_CONTAINER_PADDING;
    const offset = needsWindowing ? -(windowStart * (DOT_SIZE + DOT_GAP)) : 0;
    return (React__default.createElement("div", { "data-rev-component": 'content-slider-pagination-dots', className: className, style: { maxWidth: `${containerMaxWidth}px` } },
        React__default.createElement(motion.div, { className: 'content-slider-pagination-dots-container--rev', animate: { x: offset }, transition: { type: 'spring', duration: 0.3, bounce: 0.1 } }, Array.from({ length: totalPages }, (_, i) => (
        // eslint-disable-next-line react/forbid-elements -- low-level pagination dot; Rev Button adds unwanted structure
        React__default.createElement("button", { key: i, className: 'content-slider-pagination-dot--rev', "data-rev-current": i === currentPage, "data-rev-beyond": isBeyond(i), onClick: () => gotoPage(i), "aria-label": `Go to page ${i + 1}` }))))));
}
const ARROW_ICONS = {
    prev: 'M10.2033 7.48729C10.4866 7.18511 10.9607 7.16983 11.2629 7.45311C11.5651 7.73641 11.5804 8.2105 11.2971 8.51268L8.73066 11.25H17.0002C17.4144 11.25 17.7502 11.5858 17.7502 12C17.7502 12.4142 17.4144 12.75 17.0002 12.75H8.73066L11.2971 15.4873C11.5804 15.7895 11.5651 16.2636 11.2629 16.5469C10.9607 16.8302 10.4866 16.8149 10.2033 16.5127L6.45332 12.5127L6.36445 12.3974C6.18713 12.1143 6.21673 11.7397 6.45332 11.4873L10.2033 7.48729Z',
    next: 'M12.7373 7.45311C13.0395 7.16982 13.5136 7.18512 13.7969 7.48729L17.5469 11.4873L17.6357 11.6025C17.8131 11.8856 17.7835 12.2603 17.5469 12.5127L13.7969 16.5127C13.5136 16.8149 13.0395 16.8302 12.7373 16.5469C12.4351 16.2636 12.4198 15.7895 12.7031 15.4873L15.2695 12.75H7C6.58579 12.75 6.25 12.4142 6.25 12C6.25001 11.5858 6.58579 11.25 7 11.25H15.2695L12.7031 8.51268C12.4198 8.2105 12.4351 7.73641 12.7373 7.45311Z',
};
function MoveByItemButton({ contextValue, direction, ...buttonProps }) {
    const { prevItem, nextItem, axis } = contextValue;
    const isPrev = direction === 'prev';
    const rotation = axis === 'y' ? 'rotate(90deg)' : undefined;
    return (React__default.createElement(Button, { ...buttonProps, className: classNames('content-slider-arrow', `content-slider-arrow--${direction}`, buttonProps.className), elevation: '2', onPress: isPrev ? prevItem : nextItem, variant: buttonProps.variant ?? 'filled', isInverted: buttonProps.isInverted ?? true, rounding: buttonProps.rounding ?? 'full', ariaLabel: buttonProps.ariaLabel ?? (isPrev ? 'Previous' : 'Next'), icon: React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '24', height: '24', viewBox: '0 0 24 24', fill: 'none', style: { transform: rotation } },
            React__default.createElement("path", { fillRule: 'evenodd', clipRule: 'evenodd', d: ARROW_ICONS[direction], fill: 'currentColor' })) }, buttonProps.children));
}
function PrevItemButton(props) {
    // eslint-disable-next-line react/jsx-props-no-spreading -- forwarding caller props to MoveByItemButton
    return React__default.createElement(MoveByItemButton, { ...props, direction: 'prev' });
}
function NextItemButton(props) {
    // eslint-disable-next-line react/jsx-props-no-spreading -- forwarding caller props to MoveByItemButton
    return React__default.createElement(MoveByItemButton, { ...props, direction: 'next' });
}

export { ContentSlider, NextItemButton, PaginationDots, PrevItemButton, StyledScrollbar, useContentSliderController };
//# sourceMappingURL=ContentSlider.js.map
